import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';

interface Partner {
    _id: string;
    title: string;
    logo: string;
    link: string;
    displayOrder: number;
}

const Partners: React.FC = () => {
    const [partners, setPartners] = useState<Partner[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPartners();
    }, []);

    const fetchPartners = async () => {
        try {
            setLoading(true);
            const response = await api.get('/partners');
            setPartners(response.data.data || []);
        } catch (error) {
            console.error('Failed to fetch partners:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <PublicHeader />

            {/* Hero Section */}
            <section className="pt-32 pb-24 bg-gradient-to-br from-blue-50/40 via-white to-orange-50/30 relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl"></div>
                    <div className="absolute top-60 -left-40 w-96 h-96 bg-[#F59E0B]/10 rounded-full blur-3xl"></div>
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-5xl mx-auto text-center">
                        <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-8">
                            <span className="w-2 h-2 bg-[#3B82F6] rounded-full mr-2 animate-pulse"></span>
                            <span className="text-sm font-medium text-[#3B82F6]">Trusted Collaborations</span>
                        </div>
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-8 leading-tight">
                            <span className="text-[#3B3B3B] inline">Our </span>
                            <span className="text-[#3B82F6] inline bg-gradient-to-r from-[#3B82F6] to-blue-500 bg-clip-text text-transparent">Partners</span>
                        </h1>
                        <p className="text-xl sm:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
                            We collaborate with industry-leading organizations to deliver exceptional value and innovative solutions.
                        </p>
                    </div>
                </div>
            </section>

            {/* Partners Grid Section */}
            <section className="py-20 bg-white relative">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="text-center">
                                <div className="spinner mx-auto mb-4"></div>
                                <p className="text-gray-600">Loading partners...</p>
                            </div>
                        </div>
                    ) : partners.length > 0 ? (
                        <>
                            <div className="text-center mb-16">
                                <h2 className="text-3xl font-bold text-[#3B3B3B] mb-4">Trusted By Leading Brands</h2>
                                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                    We're proud to work alongside these amazing companies to create lasting impact.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8">
                                {partners.map((partner) => (
                                    <a
                                        key={partner._id}
                                        href={partner.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-blue-200 transition-all duration-300 p-8 flex flex-col items-center justify-center hover:-translate-y-2"
                                    >
                                        <div className="w-full h-24 flex items-center justify-center mb-6">
                                            <img
                                                src={partner.logo}
                                                alt={partner.title}
                                                className="max-w-full max-h-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300 transform group-hover:scale-110"
                                            />
                                        </div>
                                        <h3 className="text-sm font-bold text-gray-700 text-center group-hover:text-[#3B82F6] transition-colors">
                                            {partner.title}
                                        </h3>
                                    </a>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-20 bg-gray-50 rounded-3xl border border-gray-100">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg
                                    className="h-10 w-10 text-gray-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-[#3B3B3B]">No Partners Yet</h3>
                            <p className="mt-2 text-gray-500">Check back soon to see our amazing partners.</p>
                        </div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-[#3B82F6] via-blue-600 to-[#F59E0B] relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 leading-tight">
                            Interested in Partnering With Us?
                        </h2>
                        <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
                            Join our network of innovative partners and create amazing experiences together. Let's build the future.
                        </p>
                        <a
                            href="/contact"
                            className="group inline-flex items-center justify-center px-10 py-5 bg-white text-[#3B82F6] rounded-xl hover:bg-gray-50 hover:shadow-2xl transition-all duration-300 font-semibold text-base hover:-translate-y-1"
                        >
                            Get in Touch
                            <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </a>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
};

export default Partners;
